import { lazy, Suspense, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { useDriverCheck } from "@/hooks/useDriverCheck";
import { ShiftBookingPrompt } from "@/components/ShiftBookingPrompt";
import { LogOut } from "lucide-react";
import { importWithRecovery } from "@/lib/chunkRecovery";

// Lazy-loaded pages for code splitting.
// Failed chunk loads (stale assets after a new build) retry once, then reload with a cache-buster.
const lazyPage = (factory: () => Promise<{ default: ComponentType<any> }>) =>
  lazy(() => importWithRecovery(factory));
const Index = lazyPage(() => import("./pages/Index"));
const Deliveries = lazyPage(() => import("./pages/Deliveries"));
const Incidents = lazyPage(() => import("./pages/Incidents"));
const Performance = lazyPage(() => import("./pages/Performance"));
const Drivers = lazyPage(() => import("./pages/Drivers"));
const Analytics = lazyPage(() => import("./pages/Analytics"));
const Settings = lazyPage(() => import("./pages/Settings"));
const Auth = lazyPage(() => import("./pages/Auth"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const Admin = lazyPage(() => import("./pages/Admin"));
const AdminDeliveries = lazyPage(() => import("./pages/AdminDeliveries"));
const AdminCustomers = lazyPage(() => import("./pages/AdminCustomers"));
const AdminDrivers = lazyPage(() => import("./pages/AdminDrivers"));
const AdminRota = lazyPage(() => import("./pages/AdminRota"));
const AdminDriverTimetable = lazyPage(() => import("./pages/AdminDriverTimetable"));
const AdminExpenses = lazyPage(() => import("./pages/AdminExpenses"));
const AdminIncidents = lazyPage(() => import("./pages/AdminIncidents"));
const AdminProofUploads = lazyPage(() => import("./pages/AdminProofUploads"));
const AdminClockRecords = lazyPage(() => import("./pages/AdminClockRecords"));
const AdminActivityLogs = lazyPage(() => import("./pages/AdminActivityLogs"));
const AdminDeliveryZones = lazyPage(() => import("./pages/AdminDeliveryZones"));
const AdminInvoices = lazyPage(() => import("./pages/AdminInvoices"));
const AdminPresetLocations = lazyPage(() => import("./pages/AdminPresetLocations"));
const Expenses = lazyPage(() => import("./pages/Expenses"));
const Accounting = lazyPage(() => import("./pages/Accounting"));
const ProofUpload = lazyPage(() => import("./pages/ProofUpload"));
const NotificationSettings = lazyPage(() => import("./pages/NotificationSettings"));
const AdminFleet = lazyPage(() => import("./pages/AdminFleet"));
const AdminVehicleChecker = lazyPage(() => import("./pages/AdminVehicleChecker"));
const PublicHome = lazyPage(() => import("./pages/PublicHome"));
const AdminUsers = lazyPage(() => import("./pages/AdminUsers"));
const AdminReports = lazyPage(() => import("./pages/AdminReports"));
const AdminRecurringBills = lazyPage(() => import("./pages/AdminRecurringBills"));
const AdminEmailTest = lazyPage(() => import("./pages/AdminEmailTest"));
const AdminCompany = lazyPage(() => import("./pages/AdminCompany"));
const AdminRoles = lazyPage(() => import("./pages/AdminRoles"));
const UploadDocuments = lazyPage(() => import("./pages/UploadDocuments"));
const AdminDocuments = lazyPage(() => import("./pages/AdminDocuments"));
const AdminLocationTracking = lazyPage(() => import("./pages/AdminLocationTracking"));
const AdminDriverMap = lazyPage(() => import("./pages/AdminDriverMap"));
const DriverGuide = lazyPage(() => import("./pages/DriverGuide"));
const AdminExtras = lazyPage(() => import("./pages/AdminExtras"));
const AdminDriverAlerts = lazyPage(() => import("./pages/AdminDriverAlerts"));
const AdminNotes = lazyPage(() => import("./pages/AdminNotes"));
const DriverShifts = lazyPage(() => import("./pages/DriverShifts"));
const Services = lazyPage(() => import("./pages/Services"));
const MedicalCourier = lazyPage(() => import("./pages/MedicalCourier"));
const AdminVehicleCSVUpload = lazyPage(() => import("./pages/AdminVehicleCSVUpload"));
const AdminVehicleHistory = lazyPage(() => import("./pages/AdminVehicleHistory"));
const AdminDamageReports = lazyPage(() => import("./pages/AdminDamageReports"));
const AdminPBUKAudit = lazyPage(() => import("./pages/AdminPBUKAudit"));
const Kartech = lazyPage(() => import("./pages/Kartech"));
const KartechGroupPortal = lazyPage(() => import("./pages/KartechGroupPortal"));


const queryClient = new QueryClient();

// Prefetch preset locations at app startup so drivers never see "Loading shops..."
import { supabase } from "@/integrations/supabase/client";
queryClient.prefetchQuery({
  queryKey: ["preset-locations"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("preset_locations")
      .select("id, code, name, display_order")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .limit(200);
    if (error) throw error;
    return data || [];
  },
  staleTime: 5 * 60 * 1000,
});

// Minimal loading fallback that doesn't shift layout
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
  </div>
);

const MainLayout = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  useDriverCheck(); // Check if user is driver and redirect to upload page
  
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isKartech = location.pathname.startsWith('/kartech');

  // Fully standalone route (no app auth, no sidebar/header)
  if (isKartech) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/kartech" element={<Kartech />} />
          <Route path="/kartech/g/:code" element={<KartechGroupPortal />} />
          <Route path="/kartech/*" element={<Kartech />} />
        </Routes>
      </Suspense>
    );
  }

  // Public routes (no auth required)
  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/medical-courier" element={<MedicalCourier />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/driver-guide" element={<DriverGuide />} />
          <Route path="*" element={<PublicHome />} />
        </Routes>
      </Suspense>
    );
  }
  
  // Use key to force remount SidebarProvider when switching between admin/non-admin
  // This ensures mobile sidebar shows correct content
  return (
    <SidebarProvider key={isAdminRoute ? 'admin' : 'main'}>
      <div className="flex min-h-screen w-full">
        {isAdminRoute ? <AdminSidebar /> : <AppSidebar />}
        <div className="flex-1 flex flex-col">
          <header className="h-16 flex items-center justify-between border-b border-border/50 bg-background/80 backdrop-blur-xl shadow-soft px-4 md:px-6 sticky top-0 z-40">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="mr-1 md:mr-2 hover:bg-accent/50 transition-colors rounded-lg" />
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
                <img src="/logo.png" alt="GLASSY FINISH LTD" className="h-6 w-6 object-contain" />
              </div>
              <div className="hidden sm:block">
                <h2 className="text-sm font-bold text-foreground tracking-tight">
                  GLASSY FINISH LTD
                </h2>
                <p className="text-xs text-muted-foreground">Premium Logistics</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 border border-border/50">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-muted-foreground truncate max-w-32">
                  {user?.email}
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={signOut} 
                aria-label="Sign Out"
                className="h-9 px-3 border-border/50 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-all"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </header>
          <main className="flex-1 bg-background flex flex-col">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                <Route path="/deliveries" element={<ProtectedRoute><Deliveries /></ProtectedRoute>} />
                <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
                <Route path="/performance" element={<ProtectedRoute><Performance /></ProtectedRoute>} />
                <Route path="/drivers" element={<ProtectedRoute><Drivers /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
                <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} />
                <Route path="/proof-upload" element={<ProtectedRoute><ProofUpload /></ProtectedRoute>} />
                <Route path="/my-shifts" element={<ProtectedRoute><DriverShifts /></ProtectedRoute>} />
              <Route path="/driver-guide" element={<ProtectedRoute><DriverGuide /></ProtectedRoute>} />
                <Route path="/upload-documents" element={<ProtectedRoute><UploadDocuments /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/deliveries" element={<AdminRoute><AdminDeliveries /></AdminRoute>} />
                <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
                <Route path="/admin/drivers" element={<AdminRoute><AdminDrivers /></AdminRoute>} />
                <Route path="/admin/fleet" element={<AdminRoute><AdminFleet /></AdminRoute>} />
                <Route path="/admin/fleet/csv-upload" element={<AdminRoute><AdminVehicleCSVUpload /></AdminRoute>} />
                <Route path="/admin/vehicle-checker" element={<AdminRoute><AdminVehicleChecker /></AdminRoute>} />
                <Route path="/admin/vehicle-history" element={<AdminRoute><AdminVehicleHistory /></AdminRoute>} />
                <Route path="/admin/email-test" element={<AdminRoute><AdminEmailTest /></AdminRoute>} />
                <Route path="/admin/rota" element={<AdminRoute><AdminRota /></AdminRoute>} />
                <Route path="/admin/timetable" element={<AdminRoute><AdminDriverTimetable /></AdminRoute>} />
                <Route path="/admin/expenses" element={<AdminRoute><AdminExpenses /></AdminRoute>} />
                <Route path="/admin/incidents" element={<AdminRoute><AdminIncidents /></AdminRoute>} />
                <Route path="/admin/proof-uploads" element={<AdminRoute><AdminProofUploads /></AdminRoute>} />
                <Route path="/admin/clock-records" element={<AdminRoute><AdminClockRecords /></AdminRoute>} />
                <Route path="/admin/documents" element={<AdminRoute><AdminDocuments /></AdminRoute>} />
                <Route path="/admin/notifications" element={<AdminRoute><NotificationSettings /></AdminRoute>} />
                <Route path="/admin/activity-logs" element={<AdminRoute><AdminActivityLogs /></AdminRoute>} />
                <Route path="/admin/delivery-zones" element={<AdminRoute><AdminDeliveryZones /></AdminRoute>} />
                <Route path="/admin/invoices" element={<AdminRoute><AdminInvoices /></AdminRoute>} />
                <Route path="/admin/preset-locations" element={<AdminRoute><AdminPresetLocations /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
                <Route path="/admin/recurring-bills" element={<AdminRoute><AdminRecurringBills /></AdminRoute>} />
                <Route path="/admin/company" element={<AdminRoute><AdminCompany /></AdminRoute>} />
                <Route path="/admin/roles" element={<AdminRoute><AdminRoles /></AdminRoute>} />
                <Route path="/admin/location-tracking" element={<AdminRoute><AdminLocationTracking /></AdminRoute>} />
                <Route path="/admin/driver-map" element={<AdminRoute><AdminDriverMap /></AdminRoute>} />
                <Route path="/admin/extras" element={<AdminRoute><AdminExtras /></AdminRoute>} />
                <Route path="/admin/driver-alerts" element={<AdminRoute><AdminDriverAlerts /></AdminRoute>} />
                <Route path="/admin/notes" element={<AdminRoute><AdminNotes /></AdminRoute>} />
                <Route path="/admin/damage-reports" element={<AdminRoute><AdminDamageReports /></AdminRoute>} />
                <Route path="/admin/pbuk-audit" element={<AdminRoute><AdminPBUKAudit /></AdminRoute>} />
                <Route path="/auth" element={<Auth />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Footer />
          </main>
          {/* Shift booking prompt for drivers with low shift count */}
          <ShiftBookingPrompt />
        </div>
      </div>
    </SidebarProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <MainLayout />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;